import { nextTick } from "vue";

/**
 * Interruptible transition settling for sp-dialog. `settle(finalize)` waits out
 * the dialog's current enter/leave animation, then runs `finalize` only if no
 * newer transition superseded it while waiting. The monotonic token is what lets
 * a dismiss interrupt an in-progress open, and an open interrupt a close.
 *
 * @param {import("vue").Ref<HTMLDialogElement | null>} dialog - The dialog ref.
 * @returns {{ settle: (finalize: () => void) => Promise<void> }}
 */
export function useDialogTransition(dialog) {
  // Monotonic generation counter. A later transition claims a higher token, so a
  // superseded one can detect it lost and bail out.
  let transitionToken = 0;

  function requestNextTransitionToken() {
    transitionToken += 1;
    return transitionToken;
  }

  function isCurrentTransition(token) {
    return token === transitionToken;
  }

  /**
   * Resolve when the dialog's current enter/leave animation finishes, or on the
   * next frame when none is running (reduced motion, disabled animations,
   * headless). Awaits a frame first so a just-applied --state-* class can begin
   * animating before its animations are read.
   *
   * @returns {Promise<void>}
   */
  async function awaitAnimation() {
    await nextTick();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const animations = dialog.value?.getAnimations?.() ?? [];
    await Promise.allSettled(animations.map((animation) => animation.finished));
  }

  /**
   * Run `finalize` after the current transition animation, but only if no newer
   * transition superseded it while waiting.
   *
   * @param {() => void} finalize - The step to apply once the transition settles.
   * @returns {Promise<void>}
   */
  async function settle(finalize) {
    const token = requestNextTransitionToken();
    await awaitAnimation();
    if (isCurrentTransition(token)) {
      finalize();
    }
  }

  return { settle };
}
